import type { EvictorLabelSelectorExpression } from '@cast/types-workload-eviction';
import {
  Box,
  Button,
  IconButton,
  Icons,
  Input,
  Select,
  Stack,
  Typography,
  tokens,
} from '@cast/design-system';

import { EMPTY_LABEL, LABEL_OPERATORS } from '../../_constants';
import type { LabelRowError } from '../../_types';
import { operatorRequiresValues, isLabelValid } from '../../_utils';

type LabelExpressionEditorProps = {
  labels: EvictorLabelSelectorExpression[];
  disabled: boolean;
  labelErrors?: LabelRowError[];
  requireAtLeastOneLabel?: boolean;
  onChange: (labels: EvictorLabelSelectorExpression[]) => void;
};

const mapExpressionValuesToInputValue = (
  values: EvictorLabelSelectorExpression['values']
): string | undefined => {
  return values?.join(',');
};
const mapInputValueToExpressionValues = (
  value: string | undefined
): EvictorLabelSelectorExpression['values'] => {
  if (!value) {
    return undefined;
  }
  return value.split(',');
};

export const LabelExpressionEditor = ({
  labels,
  disabled,
  requireAtLeastOneLabel = false,
  onChange,
  labelErrors,
}: LabelExpressionEditorProps) => {
  const handleLabelChange = ({
    index,
    updates,
  }: {
    index: number;
    updates: Partial<EvictorLabelSelectorExpression>;
  }) => {
    const updatedLabels = labels.map((label, labelIndex) =>
      labelIndex === index ? { ...label, ...updates } : label
    );
    onChange(updatedLabels);
  };

  const handleRemoveLabel = (index: number) => {
    const updatedLabels = labels.filter(
      (_, labelIndex) => labelIndex !== index
    );

    if (requireAtLeastOneLabel && updatedLabels.length === 0) {
      onChange([{ ...EMPTY_LABEL }]);
      return;
    }

    onChange(updatedLabels);
  };

  const handleAddLabel = () => {
    onChange([...labels, { ...EMPTY_LABEL }]);
  };

  const hasLabels = labels.length > 0;
  const lastLabel = labels.at(-1);
  const shouldDisableAddLabelButton =
    disabled || (hasLabels && lastLabel && !isLabelValid(lastLabel));

  return (
    <Stack gap={8}>
      {hasLabels &&
        labels.map((label, index) => {
          const labelError = labelErrors?.find(
            (labelError) => labelError.index === index
          );

          return (
            <Stack key={index} gap={4}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 150px 1fr 32px',
                  gap: 8,
                  alignItems: 'end',
                }}
              >
                <Input
                  size="small"
                  label="Key"
                  value={label.key}
                  onChange={(event) =>
                    handleLabelChange({
                      index,
                      updates: { key: event.target.value },
                    })
                  }
                  disabled={disabled}
                  testId={`evictor-label-key-${index}`}
                />
                <Select
                  size="sm"
                  inputProps={{
                    label: 'Operator',
                  }}
                  options={LABEL_OPERATORS}
                  value={label.operator}
                  onChange={(_option, newOperator) => {
                    handleLabelChange({
                      index,
                      updates: {
                        operator: newOperator,
                      },
                    });
                  }}
                  disabled={disabled}
                  testId={`evictor-label-operator-${index}`}
                />
                {operatorRequiresValues(label.operator) ? (
                  <Input
                    label="Value"
                    size="small"
                    value={mapExpressionValuesToInputValue(label.values)}
                    onChange={(_event, value) =>
                      handleLabelChange({
                        index,
                        updates: {
                          values: mapInputValueToExpressionValues(value),
                        },
                      })
                    }
                    disabled={disabled}
                    testId={`evictor-label-values-${index}`}
                  />
                ) : (
                  <Box />
                )}
                <IconButton
                  size="sm"
                  onClick={() => handleRemoveLabel(index)}
                  disabled={
                    disabled || (requireAtLeastOneLabel && labels.length === 1)
                  }
                  testId={`evictor-remove-label-${index}`}
                >
                  <Icons.X />
                </IconButton>
              </Box>
              {labelError && (
                <Typography variant="P10R" color={tokens.theme.fg.status.error}>
                  {labelError.message}
                </Typography>
              )}
            </Stack>
          );
        })}

      <Button
        variant="text"
        size="sm"
        startIcon={<Icons.Plus />}
        onClick={handleAddLabel}
        disabled={shouldDisableAddLabelButton}
        testId="evictor-add-label-button"
      >
        Add label
      </Button>
    </Stack>
  );
};
